import { useState } from "react";
import { ChevronRight, Dot } from "lucide-react";
import {
    formatIST,
    getColorFromName,
    escapeRegex
} from "../Pages/utils/taskUtils";

const CommentsSection = ({ comments = [] }) => {
    const [showTop, setShowTop] = useState(false);

    if (!comments.length) {
        return (
            <p className="text-gray-400 text-center py-4">
                No comments yet.
            </p>
        );
    }

    const namesToHighlight = [
        ...new Set(
            comments.flatMap((c) => {
                const names = [c.createdBy];

                const assignedMatch = c.text.match(/assigned to:\s*(.*)/i);

                if (assignedMatch && assignedMatch[1]) {
                    const assignedNames = assignedMatch[1]
                        .split(",")
                        .map((n) => n.trim());

                    names.push(...assignedNames);
                }

                return names.filter(Boolean);
            })
        ),
    ];

    const numAlwaysVisible = 15;
    const totalComments = comments.length;

    const topComments =
        totalComments > numAlwaysVisible
            ? comments.slice(0, totalComments - numAlwaysVisible)
            : [];

    const bottomComments =
        totalComments > numAlwaysVisible
            ? comments.slice(totalComments - numAlwaysVisible)
            : comments;

    return (
        <div
            className="h-[73vh] p-1 flex flex-col overflow-y-auto"
            style={{
                scrollbarWidth: "thin",
                scrollbarColor: "#ccc #f9f9f9",
            }}
        >
            {topComments.length > 0 && (
                <div
                    className="flex items-center text-[#b5b5b5] mt-2 mb-2 cursor-pointer"
                    onClick={() => setShowTop((prev) => !prev)}
                >
                    <ChevronRight
                        size={14}
                        className={`transition-transform duration-300 ${showTop ? "rotate-90" : ""
                            }`}
                    />
                    <span className="ml-2 text-[11px]">
                        {showTop
                            ? "Hide"
                            : `Show ${topComments.length} Older Comments`}
                    </span>
                </div>
            )}

            {showTop &&
                topComments.map((comment) => (
                    <CommentItem
                        key={comment._id || `${comment.createdAt}-${Math.random()}`}
                        comment={comment}
                        namesToHighlight={namesToHighlight}
                    />
                ))}

            {bottomComments.map((comment) => (
                <CommentItem
                    key={comment._id || `${comment.createdAt}-${Math.random()}`}
                    comment={comment}
                    namesToHighlight={namesToHighlight}
                />
            ))}
        </div>
    );
};

const CommentItem = ({ comment, namesToHighlight = [] }) => {
    const getHighlightedText = (text) => {
        if (!text || !namesToHighlight.length) return text;

        const regex = new RegExp(
            `(${namesToHighlight.map(escapeRegex).join("|")})`,
            "gi"
        );

        const parts = text.split(regex);

        return parts.map((part, index) => {
            const match = namesToHighlight.find(
                (n) => n.toLowerCase() === part.toLowerCase()
            );

            if (match) {
                return (
                    <span
                        key={index}
                        style={{
                            backgroundColor: getColorFromName(match),
                            padding: "2px 4px",
                            borderRadius: "4px",
                            fontWeight: 500,
                        }}
                    >
                        {part}
                    </span>
                );
            }

            return part;
        });
    };

    return (
        <div className="flex justify-between gap-2 text-[11px] hover:bg-white rounded p-1">

            <div className="text-gray-600 flex-1 flex gap-2 items-start">

                <span className="w-1 h-1 bg-gray-400 rounded-full mt-[6px] flex-shrink-0"></span>

                <span className="leading-5 break-words">
                    {getHighlightedText(comment.text)}
                </span>

            </div>

            <div className="text-gray-400 text-right whitespace-nowrap text-[10px]">
                {formatIST(comment.createdAt)}
            </div>

        </div>
    );
};

export default CommentsSection;